import React from "react";
import { Box, Modal, Typography, IconButton, Button, Divider, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import HighlightOff from "@mui/icons-material/HighlightOff";
import Visibility from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";
import {
    useGetPendingDocumentsBySourceQuery,
    useUpdateDocumentApprovalStatusMutation,
} from "store/services/documentApi";

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    maxHeight: '80vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    p: 4,
};

const ApproveDocumentModal = ({ open, onClose, source, sourceRef }) => {
    const { data, isLoading } = useGetPendingDocumentsBySourceQuery(
        { source, sourceRef },
        { skip: !open || !sourceRef }
    );
    const [updateApprovalStatus, { isLoading: updating }] = useUpdateDocumentApprovalStatusMutation();

    const handleAction = async (_id, status) => {
        try {
            await updateApprovalStatus({ _id, status }).unwrap();
            toast.success(`Document ${status} successfully!`);
        } catch (err) {
            toast.error(err?.data?.message ?? `Couldn't ${status} document!`);
        }
    };

    const documents = data?.data || [];

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={style}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Pending Document Approvals</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {isLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={24} />
                    </Box>
                ) : documents.length === 0 ? (
                    <Typography color="text.secondary" align="center" py={4}>
                        No documents pending approval.
                    </Typography>
                ) : (
                    documents.map((doc) => (
                        <Box
                            key={doc._id}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            py={1.5}
                            borderBottom="1px solid #eee"
                        >
                            <Box>
                                <Typography variant="body2" fontWeight={500}>
                                    {doc.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {doc.vendorDocumentType?.name || "N/A"} • {doc.createdBy?.name || "N/A"}
                                </Typography>
                            </Box>
                            <Box display="flex" gap={1} alignItems="center">
                                {doc.attachments?.map((attachment) => (
                                    <IconButton
                                        key={attachment?._id || attachment?.url}
                                        size="small"
                                        onClick={() => window.open(attachment?.url, "_blank")}
                                    >
                                        <Visibility fontSize="small" color="primary" />
                                    </IconButton>
                                ))}
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    startIcon={<CheckCircleOutline />}
                                    disabled={updating}
                                    onClick={() => handleAction(doc._id, "approved")}
                                >
                                    Approve
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<HighlightOff />}
                                    disabled={updating}
                                    onClick={() => handleAction(doc._id, "rejected")}
                                >
                                    Reject
                                </Button>
                            </Box>
                        </Box>
                    ))
                )}
            </Box>
        </Modal>
    );
};

export default ApproveDocumentModal;